<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type EmailEgressSettings,
    type EmailEgressSettingsRequest,
    type CustomerUser,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // Email Egress integration fields
  const isEmailEgressEnabled = ref(false)
  const emailEgressAddress = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description:
        'Configure email egress integration settings. This allows JUPUS to send case data to a specified email address.',
      enableEmailEgress: 'Enable Email Egress Integration',
      emailAddress: 'Email Address',
      emailAddressPlaceholder: 'Enter email address'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description:
        'Konfigurieren Sie die E-Mail-Egress-Integrationseinstellungen. Damit kann JUPUS Falldaten an eine bestimmte E-Mail-Adresse senden.',
      enableEmailEgress: 'E-Mail Egress Integration aktivieren',
      emailAddress: 'E-Mail Adresse',
      emailAddressPlaceholder: 'E-Mail Adresse eingeben'
    }
  })

  const updateEmailEgressSettings = async () => {
    try {
      isLoading.value = true

      const data: EmailEgressSettingsRequest = {
        is_email_egress_integration_enabled: isEmailEgressEnabled.value
      }

      if (emailEgressAddress.value) {
        data.email_egress_address = emailEgressAddress.value
      }

      await SettingsService.settingsEmailEgressCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const settings = (await SettingsService.settingsEmailEgressList()) as EmailEgressSettings

      if (settings) {
        isEmailEgressEnabled.value = settings.is_email_egress_integration_enabled || false
        emailEgressAddress.value = settings.email_egress_address || ''
      }
    } catch (error) {
      console.error('Error loading Email Egress settings:', error)
      Sentry.captureException(error)
    }
  })

  watch([isEmailEgressEnabled], () => {
    if (user.value) {
      updateEmailEgressSettings()
    }
  })

  watch(emailEgressAddress, () => {
    if (user.value) {
      updateEmailEgressSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <p class="mb-4 text-sm text-gray-600">{{ t('description') }}</p>

        <div class="max-w-xl space-y-4">
          <CheckboxField
            v-model="isEmailEgressEnabled"
            name="is_email_egress_integration_enabled"
            :label="t('enableEmailEgress')"
          />

          <div v-if="isEmailEgressEnabled">
            <InputField
              v-model="emailEgressAddress"
              name="email_egress_address"
              :label="t('emailAddress')"
              :placeholder="t('emailAddressPlaceholder')"
              type="email"
            />
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
